import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Container from 'components/common/Container'
import Handlers, { HandlerOption } from 'types/Handlers'
import { useAppSelector } from '../../../../hooks/store'
import { OutputFormat } from '../../../../types/OutputFormat'
import AdvancedSettings from '../../../AdvancedSettings'
import EnhancementHistory from '../../../EnhancementHistory'
import GenerationSection from '../../../GenerationControls'
import GenerationSelectImages, {
  IItemFile,
} from '../../../GenerationControls/GenerationSelectImages/GenerationSelectImages'
import GenerationFixedButton from '../../../GenerationFixedButton'

import c from './IdleStage.module.scss'

interface IdleStageProps {
  handler: `${Handlers}`
  handlerOptions: HandlerOption[]
  imageUrls: string[]
  outputFormat: OutputFormat
  onOutputFormatChange: (newOutputFormat: OutputFormat) => void
  onProcessingStart: () => void
  setImageFiles: (newFiles: File[]) => void
  setIsAdvancedSettingsOpen: (prev: boolean) => void
  isAdvancedSettingsOpen: boolean
}

function IdleStage({
  imageUrls,
  outputFormat,
  onOutputFormatChange,
  onProcessingStart,
  handler,
  handlerOptions,
  setImageFiles,
  setIsAdvancedSettingsOpen,
  isAdvancedSettingsOpen,
}: IdleStageProps) {
  const { stage } = useAppSelector((state) => state.stage)
  const [isCompleteInputs, setIsCompleteInputs] = useState(false)

  const { t } = useTranslation()

  const handleInputChange = useCallback(
    (items: IItemFile[]) => {
      if (items.length > 0) {
        setImageFiles(items.map((value) => value.file))
        setIsCompleteInputs(true)
      } else {
        setImageFiles([])
        setIsCompleteInputs(false)
      }
    },
    [setImageFiles],
  )

  const isDisabledButton = imageUrls.length === 0

  return (
    <Container flex>
      <div className={c.content}>
        <GenerationSection>
          <GenerationSelectImages onChange={handleInputChange} />

          <GenerationFixedButton
            headingMissing={t('stages.enhancement.idle.buttonText')}
            headingEntered={t(
              'stages.enhancement.preview.buttonTextProcessing',
            )}
            headingLoader={t('stages.enhancement.processing.bannerText')}
            isDisabledButton={isDisabledButton}
            onProcessingStart={onProcessingStart}
            isDisabled={!isCompleteInputs || stage === 'processing'}
            isProcessing={stage === 'processing'}
          />
        </GenerationSection>

        <AdvancedSettings
          formatImageVisible
          imageUrls={imageUrls}
          outputFormat={outputFormat}
          onOutputFormatChange={onOutputFormatChange}
          isAdvancedSettingsOpen={isAdvancedSettingsOpen}
          setIsAdvancedSettingsOpen={setIsAdvancedSettingsOpen}
        />
      </div>
    </Container>
  )
}

export default IdleStage
